import type { ReactNode } from 'react';
import { Layout, useSidebarState } from 'react-admin';
import CustomMenu from './CustomMenu';
import CustomAppBar from './CustomAppBar';
import { useDefaultDepartmentView } from './departmentView';
import { EnvironmentBanner } from './EnvironmentBanner';
import { FeatureFlagsReady, FeatureGate } from './featureFlags/FeatureGate';
import { GuideOverlay } from '../guide/GuideOverlay';
import { AutoListFilters } from './listMemory/AutoListFilters';
import { UsageTracker } from '../usage/UsageTracker';

/**
 * Układ panelu: wąski panel boczny plus własny pasek górny.
 *
 * Port z FreightFusion (`admin/CustomLayout.jsx`, stan z 15.09.2026).
 * Zapis pochodzenia: PORT-FROM-TLSAPP.md
 *
 * Nadpisania `sx` przeniesione ze źródła bez zmian — react-admin domyślnie
 * daje panel szerszy i z marginesami, przez co listy z wieloma kolumnami
 * (a takie są w banku zleceń) nie mieszczą się na ekranie.
 *
 * Ze źródła nie przeniesiono `ImpersonationBanner` — dotyczy podszywania się
 * pod użytkownika, czyli funkcji panelu wieloklienckiego, której ta instancja
 * nie dostaje.
 */

const SIDEBAR_WIDTH_OPEN = 180;
const SIDEBAR_WIDTH_CLOSED = 47;

export const CustomLayout = ({ children }: { children: ReactNode }) => {
  const [sidebarOpen] = useSidebarState();
  // Trader zaczyna od widoku Trade, planista FTL od FTL (01.10.2026).
  useDefaultDepartmentView();
  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;

  return (
    <Layout
      menu={CustomMenu}
      appBar={CustomAppBar}
      sx={{
        '& .RaSidebar-fixed': {
          width: `${sidebarWidth}px !important`,
          minWidth: `${sidebarWidth}px !important`,
          left: '0 !important',
        },
        '& .RaSidebar-paper': {
          width: `${sidebarWidth}px !important`,
          minWidth: `${sidebarWidth}px !important`,
          padding: '0 !important',
        },
        '& .MuiDrawer-paper': {
          width: `${sidebarWidth}px !important`,
          minWidth: `${sidebarWidth}px !important`,
          padding: '0 !important',
          left: '0 !important',
        },
        '& .RaLayout-content': {
          marginLeft: '0 !important',
          paddingLeft: '8px !important',
        },
        '& .RaLayout-contentWithSidebar': { marginLeft: '0 !important' },
        '& main': { marginLeft: '0 !important', paddingLeft: '0 !important' },
        '& .RaMenu-open': { width: `${sidebarWidth}px !important` },
        '& .RaMenu-closed': { width: `${SIDEBAR_WIDTH_CLOSED}px !important` },
      }}
    >
      {/* Ograniczenie szerokości trzyma szerokie listy w kadrze zamiast
          pozwalać im rozpychać stronę w poziomie. `wsp-tresc` — treść ekranu
          z kontraktu `data-testid` paczki (S-22.10-02). */}
      <div style={{ maxWidth: `calc(100vw - ${sidebarWidth}px)` }} data-testid="wsp-tresc">
        {/* Pasek „Środowisko akceptacyjne” — tylko w paczce stagingu
            (12.10.2026); na dev i prod komponent nic nie rysuje. */}
        <EnvironmentBanner />
        {/* Pomiar użycia (22.10.2026, S-22.10-02) — bez nic na ekranie;
            zbiera tylko przy włączonym `usageTelemetry` w środowisku. */}
        <UsageTracker />
        {/* Ekran dopiero po pierwszym odczycie przełączników w sesji — bez
            mignięcia wersji „obecnej” przed „nową” (integracja przełączników
            12.10.2026); po błędzie odczytu — wersja „obecna”. Filtry list
            zapamiętywane same, bez „Zapisz obecne filtry” — za
            przełącznikiem `autoListFilters` (19.10.2026). */}
        <FeatureFlagsReady>
          <AutoListFilters>{children}</AutoListFilters>
          {/* Przewodnik po zmianach (21.10.2026, decyzja użytkownika na prośbę
              klienta) — przesuwalny panel prowadzący przez nowe funkcje; za
              przełącznikiem `guideOverlay` (S-21.10-28). */}
          <FeatureGate feature="guideOverlay">
            <GuideOverlay />
          </FeatureGate>
        </FeatureFlagsReady>
      </div>
    </Layout>
  );
};

export default CustomLayout;
